import type { UIProfile } from "../../pages/stores/startupConfiguration"

type RegistrationCleanup = () => void

export type WindowMessenger<T> = {
  /**
   * Send a message to the window
   * @param message The message to send.
   * @returns
   */
  postMessage: (message: T) => void

  /**
   * Listen for messages from the window. Returns a function to unregister the listener.
   * @param listener The function to call when a message is received.
   * @returns A function to unregister the listener.
   */
  registerMessageListener: (
    listener: (message: MessageEvent) => void
  ) => RegistrationCleanup
}

/**
 * Creates a WindowMessenger for sending and receiving messages to/from the
 * parent window of an iframe.
 */
export const createParentMessenger = <T>({
  events_target_origin,
}: UIProfile): WindowMessenger<T> => {
  const targetOrigin = normalizeOrigin(events_target_origin)

  const postMessage = (message: T) => {
    // eslint-disable-next-line no-console
    console.log("Posting message to iFrame parent", { message, targetOrigin })
    window.parent.postMessage(message, targetOrigin)
  }

  const registerMessageListener = (
    listener: (message: MessageEvent) => void
  ) => {
    const handler = (event: MessageEvent) => {
      // Ignore self-posted messages when testing outside of an iFrame
      if (event.source === window) {
        // eslint-disable-next-line no-console
        console.log("Ignoring self-posted message", { event })
        return
      }
      // Ensure the message is from the expected origin. We still want to allow
      // messages when targetOrigin is "*", which means "allow all origins".
      if (targetOrigin !== "*" && event.origin !== targetOrigin) {
        // eslint-disable-next-line no-console
        console.log("Received unexpected message origin", {
          eventOrigin: event.origin,
          expectedOrigin: targetOrigin,
        })
        return
      }
      listener(event)
    }
    window.addEventListener("message", handler)
    return () => {
      window.removeEventListener("message", handler)
    }
  }

  return { postMessage, registerMessageListener }
}

/**
 * Normalizes a target origin string for use with postMessage.
 * - Allows the wildcard "*" to pass through unchanged.
 * - Trims whitespace and parses with `new URL(...)` to extract only the
 *   scheme + host + port (i.e. the true origin), discarding any path,
 *   query, or fragment that would cause postMessage or origin-matching
 *   failures.
 * - Throws on empty, whitespace-only, or otherwise unparseable values so
 *   misconfigurations surface early.
 */
export const normalizeOrigin = (origin: string): string => {
  const trimmed = origin.trim()
  if (trimmed === "*") return "*"
  if (!trimmed) {
    throw new Error("events_target_origin must not be empty")
  }
  try {
    return new URL(trimmed).origin
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to parse events_target_origin", { origin, error })
    throw new Error("Invalid events_target_origin")
  }
}
